import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, MessageCircle, Phone, Users, Heart, Award, Stethoscope, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

const About = () => {
  const timeline = [
    { year: '1999-2001', event: 'Medical Officer at Bharat Gold Mines Hospital' },
    { year: '2001-Present', event: 'Founded charitable trust and opened free hospital' },
    { year: '2005', event: 'Launched mobile outreach clinics across KGF' },
    { year: '2015', event: 'Introduced remote diabetic monitoring with implantable glucose chip' },
    { year: '2020-Now', event: 'Expanded diagnostics with MRI and daily early-morning OPD' },
  ];

  const impacts = [
    {
      icon: <Users className="w-8 h-8 text-cyan-600" />,
      title: 'Community Outreach',
      description: 'Runs free clinics across religious and community centers to ensure access for all'
    },
    {
      icon: <Heart className="w-8 h-8 text-emerald-600" />,
      title: 'Remote Monitoring',
      description: 'Pioneered implantable glucose monitoring devices — first in the region'
    },
    {
      icon: <Stethoscope className="w-8 h-8 text-blue-600" />,
      title: 'Full Diagnostics',
      description: 'Offers MRI, labs, and pathology at no cost to former mine workers'
    },
    {
      icon: <Award className="w-8 h-8 text-purple-600" />,
      title: 'Digital Healthcare Advocacy',
      description: 'Advocates for government adoption of digital healthcare in rural areas'
    },
  ];

  return (
    <main className="py-12" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
              Dr. B. Rajendra Kumar
            </h1>
            <h2 className="text-2xl sm:text-3xl text-cyan-700 font-semibold mb-6">
              A Lifeline for KGF
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              25+ years of free, technology-driven care for displaced mine workers and their families.
            </p>
          </div>
        </div>
      </section>

      {/* Two Column Story Layout */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12">
            {/* Left Column - Story */}
            <div className="md:col-span-2 space-y-6">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">The Story</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  When the Kolar Gold Fields fell silent, the health of its people spoke loudly. 
                  Dr. B. Rajendra Kumar — former Medical Officer at Bharat Gold Mines Hospital — 
                  transformed that silence into care. For over 25 years he has run a charitable hospital 
                  and outreach clinics that deliver free, modern healthcare to thousands affected by 
                  unemployment, toxic exposure, and poverty.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Dr. Kumar opens his doors at 5:00 AM, meeting hundreds who come for consultations, 
                  diagnostics, and follow-up care. His trust-funded hospital provides MRI scans, pathology, 
                  and specialist consultations under one roof. Beyond the clinic, he runs regular free camps 
                  near temples, mosques, and churches to reach the housebound and elderly.
                </p>
              </div>

              {/* Impact Section */}
              <div className="pt-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-6">Impact & Innovations</h3>
                <div className="grid sm:grid-cols-2 gap-6">
                  {impacts.map((impact, index) => (
                    <Card key={index} className="border-2 border-gray-100 hover:border-cyan-200 card-hover">
                      <CardContent className="p-6">
                        <div className="mb-4">{impact.icon}</div>
                        <h4 className="text-xl font-semibold mb-2 text-gray-900">{impact.title}</h4>
                        <p className="text-gray-600 text-sm">{impact.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Quick Facts Card */}
            <div className="md:col-span-1">
              <Card className="sticky top-20 bg-gradient-to-br from-cyan-600 to-emerald-600 text-white border-0 shadow-2xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6">Quick Facts</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm opacity-90 mb-1">Specialty</div>
                      <div className="font-semibold">Cardiology & Community Health</div>
                    </div>
                    <div>
                      <div className="text-sm opacity-90 mb-1">Years of Service</div>
                      <div className="font-semibold">25+ years</div>
                    </div>
                    <div>
                      <div className="text-sm opacity-90 mb-1">Patients Helped</div>
                      <div className="font-semibold">Thousands (daily OPD queues)</div>
                    </div>
                    <div>
                      <div className="text-sm opacity-90 mb-1">Clinic Hours</div>
                      <div className="font-semibold">5:00 AM – 11:00 AM (OPD), Mon–Sat</div>
                    </div>
                    <div>
                      <div className="text-sm opacity-90 mb-1">Contact</div>
                      <div className="font-semibold">+91 9876543210</div>
                    </div>
                  </div>

                  <div className="mt-8 space-y-3">
                    <Link to="/appointments" className="block">
                      <Button className="w-full bg-white text-cyan-600 hover:bg-gray-100">
                        <Calendar className="w-4 h-4 mr-2" />
                        Book Appointment
                      </Button>
                    </Link>
                    <a
                      href="https://wa.me/919876543210"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <Button variant="outline" className="w-full border-2 border-white text-white hover:bg-white/10">
                        <MessageCircle className="w-4 h-4 mr-2" />
                        WhatsApp Now
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-gray-900 mb-12 text-center">Journey Timeline</h3>
          <div className="space-y-8">
            {timeline.map((item, index) => (
              <div key={index} className="flex gap-6 items-start" data-testid={`timeline-item-${index}`}>
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-cyan-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                    {index + 1}
                  </div>
                </div>
                <div className="flex-1 bg-white p-6 rounded-xl shadow-md">
                  <div className="text-sm font-semibold text-cyan-600 mb-1">{item.year}</div>
                  <div className="text-lg text-gray-900">{item.event}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 md:p-12 rounded-2xl shadow-lg text-center">
            <div className="text-6xl text-cyan-600 mb-4">“</div>
            <p className="text-2xl text-gray-800 font-medium mb-6 italic">
              He is a living God to me — he saved my life and saved my family from medical ruin.
            </p>
            <div className="flex items-center gap-4 justify-center">
              <div className="w-12 h-12 bg-cyan-600 rounded-full flex items-center justify-center text-white font-bold">
                S
              </div>
              <div>
                <div className="font-semibold text-gray-900">Selva, 70</div>
                <div className="text-sm text-gray-600">Ongoing cardiac patient</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Closing Section */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 to-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl font-bold mb-6">Support the Mission</h3>
          <p className="text-xl mb-8 opacity-90">
            In a town scarred by industrial abandonment, Dr. Kumar’s work restores health and dignity. 
            His blend of compassion and technology proves that even small, sustained efforts can change generations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/appointments">
              <Button size="lg" className="bg-white text-cyan-600 hover:bg-gray-100">
                Book Free Appointment
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
